import LoadingDots from 'core/modules/atoms/LoadingDots';
import Textarea from 'core/modules/atoms/Textarea';
import * as coreEntities from 'core/store/index';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import SelectLearningObjectives from '../SelectLearningObjectives/SelectLearningObjectives';
import './_evaluation-criteria-selector.scss';

/**
 * EvaluationCriteriaSelector - Composite component for selecting evaluation criteria
 *
 * Shows learning objectives tree selector + textarea if learning objectives exist,
 * or only textarea if no learning objectives are available.
 *
 * @param {Object} props - Component props
 * @returns {JSX.Element} Rendered component
 */
function EvaluationCriteriaSelector({ onChange, required, value }) {
  const { t } = useTranslation(['chatbot', 'contents']);
  const dispatch = useDispatch();

  // Get learning objectives from Redux
  const learningObjectives = useSelector((state) => coreEntities.learningObjectives.selectors.getLearningObjectives(state));

  const [isLoading, setIsLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);

  // Fetch learning objectives on mount (if not already loaded)
  useEffect(() => {
    if (!hasFetched) {
      dispatch(coreEntities.learningObjectives.actions.getLearningObjectives());
      setHasFetched(true);
    }
  }, [dispatch, hasFetched]);

  // Track loading state - stop loading once we have a response (even empty array)
  useEffect(() => {
    if (hasFetched && learningObjectives !== undefined) {
      // Small delay to ensure Redux state is settled
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [hasFetched, learningObjectives]);

  // TEMPORARY DISABLED
  // const hasLearningObjectives = learningObjectives?.length > 0;
  const hasLearningObjectives = false;

  // Handle selected learning objectives change
  const handleSelectedChange = (selectedArray) => {
    onChange({
      ...value,
      selected: selectedArray,
    });
  };

  // Handle additional criteria text change
  const handleAdditionalChange = (e) => {
    onChange({
      ...value,
      additional: e.currentTarget.value,
    });
  };

  if (isLoading) {
    return (
      <div className="evaluation-criteria-selector evaluation-criteria-selector--loading">
        <LoadingDots />
      </div>
    );
  }

  // Conditional rendering based on whether learning objectives exist
  if (hasLearningObjectives) {
    return (
      <div className="evaluation-criteria-selector">
        {/* Learning Objectives Tree Selector - Dropdown Style */}
        <div className="evaluation-criteria-selector__field">
          <label className="evaluation-criteria-selector__label">{t('chatbot:teacher_tools_evaluation_criteria_label')}</label>
          <SelectLearningObjectives
            onChange={handleSelectedChange}
            placeholder={t('chatbot:teacher_tools_evaluation_criteria_placeholder')}
            showBadge={false}
            value={value?.selected || []}
            variant="input"
          />
        </div>

        {/* Additional Criteria Textarea */}
        <div className="evaluation-criteria-selector__field">
          <Textarea
            label={t('chatbot:teacher_tools_additional_criteria_label')}
            name="additionalCriteria"
            onChange={handleAdditionalChange}
            placeholder={t('chatbot:teacher_tools_additional_criteria_placeholder')}
            rows={3}
            value={value?.additional || ''}
          />
        </div>
      </div>
    );
  }

  // Fallback: Only textarea when no learning objectives exist
  return (
    <div className="evaluation-criteria-selector">
      <Textarea
        label={t('chatbot:teacher_tools_evaluation_criteria_label')}
        name="evaluationCriteria"
        onChange={handleAdditionalChange}
        placeholder={t('chatbot:teacher_tools_evaluation_criteria_placeholder')}
        required={required}
        rows={3}
        value={value?.additional || ''}
      />
    </div>
  );
}

EvaluationCriteriaSelector.propTypes = {
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  value: PropTypes.shape({
    additional: PropTypes.string,
    selected: PropTypes.array,
  }),
};

EvaluationCriteriaSelector.defaultProps = {
  required: false,
  value: { additional: '', selected: [] },
};

export default EvaluationCriteriaSelector;
